import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  acknowledgeInboxNotificationId,
  clearAcknowledgedInboxNotificationIds,
  readAcknowledgedInboxNotificationIds,
} from '@/lib/inboxAcknowledgedNotifications';
import { processInboxNotificationAction } from '@/lib/notificationUtils';
import { useMarkNotificationsAsRead } from '@/hooks/queries/notification';
import type { UserNotification } from '@/types/notifications';

/** Inbox status filter — 'all' maps to no `status` param (API returns read + unread). */
export type InboxStatusFilter = 'unread' | 'read' | 'all';

type InboxSessionContextValue = {
  /** Notification ids clicked this inbox session (hide unread dot before refetch). */
  acknowledgedNotificationIds: ReadonlySet<number>;
  acknowledgeNotification: (notificationId: number) => void;
  clearAcknowledgedNotifications: () => void;
  /** Last notification id processed by Next Unread (null = start from first unread). */
  nextUnreadCursorId: number | null;
  setNextUnreadCursorId: (notificationId: number | null) => void;
  clearNextUnreadCursor: () => void;
  /** Inbox status filter — defaults to 'unread'. */
  statusFilter: InboxStatusFilter;
  setStatusFilter: (statusFilter: InboxStatusFilter) => void;
};

const InboxSessionContext = createContext<InboxSessionContextValue | null>(null);

/**
 * Sorts notifications by notifiedAt descending (newest first).
 */
function sortNotificationsByNotifiedAtDesc(
  notifications: UserNotification[],
): UserNotification[] {
  return [...notifications].sort((a, b) => {
    const diff = b.notifiedAt - a.notifiedAt;
    return diff !== 0 ? diff : b.id - a.id;
  });
}

/**
 * Tracks inbox notifications acknowledged in-session and Next Unread cursor state.
 */
export function InboxSessionProvider({ children }: { children: ReactNode }) {
  const [acknowledgedNotificationIds, setAcknowledgedNotificationIds] = useState<
    Set<number>
  >(() => readAcknowledgedInboxNotificationIds());
  const [nextUnreadCursorId, setNextUnreadCursorId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<InboxStatusFilter>('unread');

  const acknowledgeNotification = useCallback((notificationId: number) => {
    const next = acknowledgeInboxNotificationId(notificationId);
    setAcknowledgedNotificationIds(next);
  }, []);

  const clearAcknowledgedNotifications = useCallback(() => {
    clearAcknowledgedInboxNotificationIds();
    setAcknowledgedNotificationIds(new Set());
  }, []);

  const clearNextUnreadCursor = useCallback(() => {
    setNextUnreadCursorId(null);
  }, []);

  const value = useMemo(
    () => ({
      acknowledgedNotificationIds,
      acknowledgeNotification,
      clearAcknowledgedNotifications,
      nextUnreadCursorId,
      setNextUnreadCursorId,
      clearNextUnreadCursor,
      statusFilter,
      setStatusFilter,
    }),
    [
      acknowledgedNotificationIds,
      acknowledgeNotification,
      clearAcknowledgedNotifications,
      nextUnreadCursorId,
      clearNextUnreadCursor,
      statusFilter,
    ],
  );

  return (
    <InboxSessionContext.Provider value={value}>{children}</InboxSessionContext.Provider>
  );
}

/**
 * Inbox session helpers for notification read indicators and Next Unread flow.
 */
export function useInboxSession(): InboxSessionContextValue {
  const context = useContext(InboxSessionContext);
  if (!context) {
    throw new Error('useInboxSession must be used within InboxSessionProvider');
  }
  return context;
}

type UseNextUnreadNotificationOptions = {
  /** Notifications already loaded on the current page — no extra API calls needed. */
  notifications: UserNotification[];
  pageNo: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

type UseNextUnreadNotificationResult = {
  /** Opens the next unread notification using only already-loaded data. */
  openNextUnread: () => void;
  /** Whether there are any unread notifications remaining. */
  hasMoreUnread: boolean;
};

/**
 * Sequentially opens unread notifications using only already-loaded page data.
 * Zero extra API calls — navigates using the current page's notification list.
 */
export function useNextUnreadNotification({
  notifications,
  pageNo,
  totalPages,
  onPageChange,
}: UseNextUnreadNotificationOptions): UseNextUnreadNotificationResult {
  const navigate = useNavigate();
  const markAsReadMutation = useMarkNotificationsAsRead();
  const {
    acknowledgeNotification,
    acknowledgedNotificationIds,
    nextUnreadCursorId,
    setNextUnreadCursorId,
    clearNextUnreadCursor,
  } = useInboxSession();

  // Accurate local unread count — accounts for items acknowledged this session.
  const localUnread = notifications.filter(
    (n) => !n.isRead && !acknowledgedNotificationIds.has(n.id),
  );
  const hasMoreUnread = localUnread.length > 0 || pageNo < totalPages;

  // Guard against rapid double-clicks advancing the page twice before isFetching updates.
  const advancedToPage = useRef<number | null>(null);
  const prevPageNo = useRef(pageNo);
  if (prevPageNo.current !== pageNo) {
    prevPageNo.current = pageNo;
    advancedToPage.current = null;
  }

  const openNextUnread = useCallback(() => {
    const sorted = sortNotificationsByNotifiedAtDesc(notifications);
    const unread = sorted.filter(
      (n) => !n.isRead && !acknowledgedNotificationIds.has(n.id),
    );

    let nextNotification: UserNotification | undefined;

    if (nextUnreadCursorId === null) {
      nextNotification = unread[0];
    } else {
      const cursorIdx = unread.findIndex((n) => n.id === nextUnreadCursorId);
      if (cursorIdx !== -1) {
        nextNotification = unread[cursorIdx + 1];
      } else {
        // Cursor item is no longer unread (read externally). Advance past its
        // position in the full sorted list rather than restarting from the top.
        const cursorInAll = sorted.findIndex((n) => n.id === nextUnreadCursorId);
        nextNotification =
          cursorInAll === -1
            ? unread[0]
            : sorted
                .slice(cursorInAll + 1)
                .find((n) => !n.isRead && !acknowledgedNotificationIds.has(n.id));
      }
    }

    if (nextNotification) {
      setNextUnreadCursorId(nextNotification.id);
      processInboxNotificationAction(nextNotification, {
        navigate,
        acknowledgeNotification,
        markAsRead: (params) => markAsReadMutation.mutate(params),
      });
      return;
    }

    // No more unreads on this page — advance to the next page.
    if (pageNo < totalPages && advancedToPage.current !== pageNo + 1) {
      advancedToPage.current = pageNo + 1;
      clearNextUnreadCursor();
      onPageChange(pageNo + 1);
    }
  }, [
    acknowledgeNotification,
    acknowledgedNotificationIds,
    clearNextUnreadCursor,
    markAsReadMutation,
    navigate,
    nextUnreadCursorId,
    notifications,
    onPageChange,
    pageNo,
    setNextUnreadCursorId,
    totalPages,
  ]);

  return { openNextUnread, hasMoreUnread };
}
